import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import Table from "examples/Tables/Table";

import { api } from "../../api";
import { IoGlobe, IoRocketSharp, IoCheckmarkCircle, IoWarning } from "react-icons/io5";

export default function Dashboard() {
  const history = useHistory();
  const [stats, setStats] = useState({
    metrics: { totalSites: 0, activeSites: 0, totalDeployments: 0, successfulDeployments: 0, failedDeployments: 0 },
    recentDeployments: [],
    activeSites: [],
  });
  const [loading, setLoading] = useState(true);
  const [logModal, setLogModal] = useState({ open: false, logs: "", title: "", loading: false });

  const fetchDashboardData = async () => {
    try {
      const res = await api.getStats();
      if (res.success && res.data) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 6000);
    return () => clearInterval(interval);
  }, []);

  const viewLogs = async (depId, commitMsg) => {
    setLogModal({ open: true, logs: "", title: `Deployment #${depId} - ${commitMsg || "Logs"}`, loading: true });
    try {
      const res = await api.getDeploymentLogs(depId);
      if (res.success) {
        setLogModal((prev) => ({ ...prev, logs: res.data.logs || "No logs generated for this deployment.", loading: false }));
      }
    } catch (err) {
      setLogModal((prev) => ({ ...prev, logs: "Failed to load logs: " + err.message, loading: false }));
    }
  };

  // Sites Table Rows
  const siteColumns = [
    { name: "site", align: "left" },
    { name: "subdomain", align: "left" },
    { name: "branch", align: "center" },
    { name: "status", align: "center" },
    { name: "last deployed", align: "center" },
    { name: "action", align: "right" },
  ];

  const siteRows = stats.activeSites.map((site) => ({
    site: (
      <VuiBox display="flex" alignItems="center">
        <VuiBox
          width="36px"
          height="36px"
          borderRadius="10px"
          bgColor="info"
          display="flex"
          justifyContent="center"
          alignItems="center"
          mr={2}
        >
          <IoGlobe color="#fff" size="18px" />
        </VuiBox>
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" color="white" fontWeight="medium">
            {site.name}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            {site.repoName || "Custom Git"}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    subdomain: (
      <a href={site.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
        <VuiTypography variant="button" color="info" fontWeight="medium">
          {site.subdomain}.web.int.yt ↗
        </VuiTypography>
      </a>
    ),
    branch: (
      <VuiBadge
        color="dark"
        variant="gradient"
        size="sm"
        badgeContent={site.branch || "main"}
      />
    ),
    status: (
      <VuiBadge
        color={site.status === "live" ? "success" : site.status === "building" ? "info" : "warning"}
        variant="standard"
        size="sm"
        badgeContent={site.status ? site.status.toUpperCase() : "PENDING"}
      />
    ),
    "last deployed": (
      <VuiTypography variant="caption" color="text" fontWeight="medium">
        {site.lastDeployedAt ? new Date(site.lastDeployedAt).toLocaleString() : "Never"}
      </VuiTypography>
    ),
    action: (
      <VuiBox display="flex" justifyContent="flex-end" gap={1}>
        <VuiButton
          variant="outlined"
          color="info"
          size="small"
          onClick={() => history.push(`/sites/${site.id}`)}
        >
          Manage
        </VuiButton>
      </VuiBox>
    ),
  }));

  // Deployments Table Rows
  const depColumns = [
    { name: "deployment", align: "left" },
    { name: "site", align: "left" },
    { name: "commit", align: "left" },
    { name: "status", align: "center" },
    { name: "duration", align: "center" },
    { name: "created", align: "center" },
    { name: "logs", align: "right" },
  ];

  const depRows = stats.recentDeployments.map((dep) => ({
    deployment: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        #{dep.id}
      </VuiTypography>
    ),
    site: (
      <VuiTypography variant="button" color="text" fontWeight="medium">
        {dep.siteName}
      </VuiTypography>
    ),
    commit: (
      <VuiBox display="flex" flexDirection="column">
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {dep.commitMessage ? (dep.commitMessage.length > 35 ? dep.commitMessage.slice(0, 35) + "..." : dep.commitMessage) : "Initial deploy"}
        </VuiTypography>
        <VuiTypography variant="caption" color="text">
          {dep.commitSha ? dep.commitSha.slice(0, 7) : "HEAD"} ({dep.branch})
        </VuiTypography>
      </VuiBox>
    ),
    status: (
      <VuiBadge
        color={dep.status === "live" ? "success" : dep.status === "building" ? "info" : dep.status === "failed" ? "error" : "warning"}
        variant="standard"
        size="sm"
        badgeContent={dep.status.toUpperCase()}
      />
    ),
    duration: (
      <VuiTypography variant="caption" color="text" fontWeight="medium">
        {dep.durationMs ? `${(dep.durationMs / 1000).toFixed(1)}s` : "-"}
      </VuiTypography>
    ),
    created: (
      <VuiTypography variant="caption" color="text" fontWeight="medium">
        {new Date(dep.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </VuiTypography>
    ),
    logs: (
      <VuiButton
        variant="text"
        color="info"
        size="small"
        onClick={() => viewLogs(dep.id, dep.commitMessage)}
      >
        View Logs
      </VuiButton>
    ),
  }));

  const successRate = stats.metrics.totalDeployments > 0
    ? Math.round((stats.metrics.successfulDeployments / stats.metrics.totalDeployments) * 100)
    : 100;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Metric Cards */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "TOTAL SITES", fontWeight: "regular" }}
                count={String(stats.metrics.totalSites)}
                percentage={{ color: "success", text: `${stats.metrics.activeSites} active` }}
                icon={{ color: "info", component: <IoGlobe size="20px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "DEPLOYMENTS", fontWeight: "regular" }}
                count={String(stats.metrics.totalDeployments)}
                percentage={{ color: "info", text: "Global builds" }}
                icon={{ color: "info", component: <IoRocketSharp size="20px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "SUCCESS RATE", fontWeight: "regular" }}
                count={`${successRate}%`}
                percentage={{ color: successRate >= 90 ? "success" : "warning", text: `${stats.metrics.successfulDeployments} passed` }}
                icon={{ color: "info", component: <IoCheckmarkCircle size="20px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "FAILED BUILDS", fontWeight: "regular" }}
                count={String(stats.metrics.failedDeployments)}
                percentage={{ color: stats.metrics.failedDeployments === 0 ? "success" : "error", text: "Diagnosable in logs" }}
                icon={{ color: "info", component: <IoWarning size="20px" color="white" /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>

        {/* Quick Launch & Actions Bar */}
        <VuiBox mb={3}>
          <Card>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} p={1}>
              <VuiBox>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Static Website Hosting Platform
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular">
                  Connected to Intent Cloud edge infrastructure • Zero-downtime atomic deployments
                </VuiTypography>
              </VuiBox>
              <VuiBox display="flex" gap={2}>
                <VuiButton
                  variant="gradient"
                  color="info"
                  onClick={() => history.push("/sites")}
                >
                  + New Site
                </VuiButton>
                <VuiButton
                  variant="outlined"
                  color="white"
                  onClick={() => history.push("/github")}
                >
                  GitHub Repos
                </VuiButton>
              </VuiBox>
            </VuiBox>
          </Card>
        </VuiBox>

        {/* Live Sites Table */}
        <VuiBox mb={3}>
          <Card>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <VuiBox>
                <VuiTypography variant="lg" color="white" fontWeight="bold">
                  Active Sites
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular">
                  Sites deployed and served with automated subdomains
                </VuiTypography>
              </VuiBox>
              <VuiButton variant="text" color="info" onClick={() => history.push("/sites")}>
                View All Sites &rarr;
              </VuiButton>
            </VuiBox>
            {stats.activeSites.length === 0 && !loading ? (
              <VuiBox py={4} textAlign="center">
                <VuiTypography variant="button" color="text" display="block" mb={2}>
                  You have not created any sites yet.
                </VuiTypography>
                <VuiButton variant="gradient" color="info" size="small" onClick={() => history.push("/sites")}>
                  Deploy Your First Site
                </VuiButton>
              </VuiBox>
            ) : (
              <Table columns={siteColumns} rows={siteRows} />
            )}
          </Card>
        </VuiBox>

        {/* Recent Deployments Table */}
        <VuiBox mb={3}>
          <Card>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <VuiBox>
                <VuiTypography variant="lg" color="white" fontWeight="bold">
                  Recent Deployments
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular">
                  Real-time pipeline build outputs and status
                </VuiTypography>
              </VuiBox>
              <VuiButton variant="text" color="info" onClick={() => history.push("/deployments")}>
                View All Deployments &rarr;
              </VuiButton>
            </VuiBox>
            {stats.recentDeployments.length === 0 && !loading ? (
              <VuiBox py={4} textAlign="center">
                <VuiTypography variant="button" color="text">
                  No deployment history found.
                </VuiTypography>
              </VuiBox>
            ) : (
              <Table columns={depColumns} rows={depRows} />
            )}
          </Card>
        </VuiBox>
      </VuiBox>

      {/* Build Logs Dialog */}
      <Dialog
        open={logModal.open}
        onClose={() => setLogModal({ ...logModal, open: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#070d24",
            backgroundImage: "none",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "16px",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <VuiTypography variant="h6" color="white">
            {logModal.title}
          </VuiTypography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.08)" }}>
          {logModal.loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress color="info" />
            </VuiBox>
          ) : (
            <VuiBox
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                backgroundColor: "#030614",
                p: 2,
                borderRadius: "8px",
                overflowX: "auto",
                maxHeight: "500px",
                color: "#00e676",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {logModal.logs}
            </VuiBox>
          )}
        </DialogContent>
        <DialogActions>
          <VuiButton color="info" onClick={() => setLogModal({ ...logModal, open: false })}>
            Close
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
